import type { ReactNode } from 'react';
import { usePermission, LEVEL } from '../../hooks/usePermission';
import { useStagedPermission } from '../../hooks/usePermissionEnforcement';
import { useGlobalStore } from '../../stores/globalStore';
import { useIsPsiStaff } from '../../utils/psiStaff';
import NoAccess from './NoAccess';
import SessionLoadError, { PermissionsLoading } from './SessionLoadError';
import { STAGED_ROUTE_RULES, stagedRouteAllows } from './stagedRouteRules';

interface SecureRouteProps {
  /** The security field name on the role (e.g. 'dietech_Menu_Cycle_Level') */
  securityField: string;
  /** Minimum required level (defaults to VIEW = 1) */
  minLevel?: number;
  /** Feature name for the "no access" message */
  feature?: string;
  /**
   * PSI staff (a psibuffalo.com login) may open it too, whatever their role allows, as the API's
   * RequirePsiStaffOrRoleLevel lets them. App.tsx's securedOrPsi; Logged In Users (Jeff, 2026-11-03).
   */
  psiStaffToo?: boolean;
  children: ReactNode;
}

/**
 * Wraps a page component and blocks rendering if the user's security level
 * for the given field is below the minimum required level.
 *
 * Fails closed: until the role has loaded nothing renders (a spinner, or the
 * sign-in error with Try again when the role could not be read). MainLayout
 * already holds its pages back the same way; this covers the full-screen
 * routes outside it (Kitchen).
 *
 * A field listed in STAGED_ROUTE_RULES also needs that rule's level, but only when the connection
 * is in Enforce mode (the API stages the same rule).
 */
export default function SecureRoute({
  securityField,
  minLevel = LEVEL.VIEW,
  feature,
  psiStaffToo = false,
  children,
}: SecureRouteProps) {
  const { getLevel, roleLoaded } = usePermission();
  const sessionLoadError = useGlobalStore((s) => s.sessionLoadError);
  const isPsiStaff = useIsPsiStaff();

  if (!roleLoaded) {
    return sessionLoadError ? <SessionLoadError message={sessionLoadError} /> : <PermissionsLoading />;
  }

  if (psiStaffToo && isPsiStaff) return <>{children}</>;

  if (getLevel(securityField) < minLevel) {
    return <NoAccess feature={feature} />;
  }

  if (STAGED_ROUTE_RULES[securityField]) {
    return <StagedRouteGate securityField={securityField} feature={feature}>{children}</StagedRouteGate>;
  }

  return <>{children}</>;
}

/** Only mounted for a field with a staged rule, so other routes never ask for the mode. */
function StagedRouteGate({ securityField, feature, children }: { securityField: string; feature?: string; children: ReactNode }) {
  const { enforced, role } = useStagedPermission();
  if (!stagedRouteAllows(enforced, role, securityField)) return <NoAccess feature={feature} />;
  return <>{children}</>;
}
